import React, { Fragment, useEffect, useState } from "react";
import makeStyles from "@mui/styles/makeStyles";
import {
  append,
  concat,
  includes,
  join,
  mergeRight,
  uniq,
  without,
} from "ramda";
import { useSelected } from "../OrganizeProvider";
import { useSearchQuery } from "../../common/use-searchQuery";
import { useContentByStatus } from "../../creatorServices";
import { notBlank } from "../../../util/ext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleDown } from "@fortawesome/pro-regular-svg-icons";
import { useUnorganizedContentGroups } from "../../creatorServices";
import { useLocation } from "react-router-dom";
import { deduplicate, sortByLastUpdated } from "../../../util/mediaUtil";

import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";

import ContentGroupsPanel from "./ContentGroupsPanel";
import ContentGroupFilesBrowser from "./ContentGroupFilesBrowser";
import ContentSourcesAutocomplete from "../common/ContentSourcesAutocomplete";
import OrganizeButton from "../common/OrganizeButton";
import OrganizeContentGroupsBrowser from "./OrganizeContentGroupsBrowser";
import RealTimeSearchBar from "../../common/RealTimeSearchBar";

const useStyles = makeStyles(theme => ({
  endIcon: {
    color: theme.palette.primary.brandPrimary,
  },
}));

const initialContentState = {
  appliedQuery: "",
  page: 0,
  limit: 10,
  status: "approved",
  orderBy: "media.auditAdded",
  orderDirection: "desc",
  hasParent: false,
  source: {
    projectName: "",
    projectKey: "",
  },
};

const getInitialContentSearchState = initialState =>
  mergeRight(initialContentState, initialState);

const initialContentGroupsState = {
  appliedQuery: "",
  page: 0,
  limit: 10,
  orderBy: "auditUpdated",
  orderDirection: "desc",
  hasParent: false,
  mediaTypes: ["anthology", "collection"],
};

const UngroupedItemsBrowser = props => {
  const { SnackAlertHandler, view } = props;
  const classes = useStyles();
  const location = useLocation();
  const { showAlert } = SnackAlertHandler;
  const { selectedContent } = useSelected();

  //-- Manage Menu States
  const [anchorElType, setAnchorElType] = useState(null);
  const handleClick = event => {
    event.stopPropagation();
    setAnchorElType(event.currentTarget);
  };

  const handleTypeClose = event => {
    event.stopPropagation();
    setAnchorElType(null);
  };

  const onTypeClick = type => event => {
    event.stopPropagation();

    const newTypes = event.target.checked
      ? uniq(append(type, initialContentGroupsState.mediaTypes))
      : without(type, initialContentGroupsState.mediaTypes);

    contentGroupsQuery.handleUpdateMediaTypes(newTypes);
  };

  //-- Manage Content Groups Search State
  const contentGroupsQuery = useSearchQuery(initialContentGroupsState);
  const [contentGroupsQueryState, setContentGroupsQueryState] = useState({
    ...initialContentGroupsState,
    limit: initialContentGroupsState.limit,
    offset: contentGroupsQuery.state.limit * contentGroupsQuery.state.page,
    sort: `${contentGroupsQuery.state.orderBy} ${contentGroupsQuery.state.orderDirection}`,
    hasParent: false,
    mediaType: join(",", contentGroupsQuery.state.mediaTypes),
  });

  useEffect(() => {
    const { limit, page, appliedQuery } = contentGroupsQuery.state;
    const sort =
      contentGroupsQuery.state.orderBy === null
        ? `${initialContentGroupsState.orderBy} ${initialContentGroupsState.orderDirection}`
        : `${contentGroupsQuery.state.orderBy} ${contentGroupsQuery.state.orderDirection}`;

    setContentGroupsQueryState({
      limit,
      offset: limit * page,
      sort,
      hasParent: false,
      mediaType: join(",", contentGroupsQuery.state.mediaTypes),
      q: appliedQuery,
    });
  }, [contentGroupsQuery.state]);

  const contentGroupsResults = useUnorganizedContentGroups(
    contentGroupsQueryState,
  );

  const [contentGroups, setContentGroups] = useState([]);
  useEffect(() => {
    if (contentGroupsResults.isSuccess) {
      setContentGroups(prev => {
        const previous = contentGroupsQuery.state.page === 0 ? [] : prev;
        const groups = concat(previous, contentGroupsResults.data.contents);
        return sortByLastUpdated(deduplicate(groups));
      });
    }
  }, [
    contentGroupsQuery.state.page,
    contentGroupsResults.isSuccess,
    contentGroupsResults.data,
  ]);

  //-- Manage Content Search State
  const initialContentState = getInitialContentSearchState(
    notBlank(location.state) ? location.state.searchState : {},
  );
  const searchQuery = useSearchQuery(initialContentState);
  const [contentSearchState, setContentSearchState] = useState({
    ...initialContentState,
    offset: searchQuery.state.limit * searchQuery.state.page,
    sort: `${searchQuery.state.orderBy} ${searchQuery.state.orderDirection}`,
    source: searchQuery.state.source,
  });

  useEffect(() => {
    const { appliedQuery, limit, page } = searchQuery.state;
    const sort =
      searchQuery.state.orderBy === null
        ? `${initialContentState.orderBy} ${initialContentState.orderDirection}`
        : `${searchQuery.state.orderBy} ${searchQuery.state.orderDirection}`;

    setContentSearchState({
      ...searchQuery.state,
      sort,
      offset: limit * page,
      q: appliedQuery,
    });
  }, [
    searchQuery.state,
    initialContentState.orderBy,
    initialContentState.orderDirection,
  ]);

  const contentResults = useContentByStatus({
    ...contentSearchState,
    source: contentSearchState.source.projectKey,
  });

  return (
    <OrganizeContentGroupsBrowser
      view={view}
      header={<Typography variant="h6">Unorganized</Typography>}
      contentGroupsPanel={
        <ContentGroupsPanel
          title="Content Groups"
          loading={contentGroupsResults.isLoading}
          hasMore={
            contentGroupsResults.isSuccess
              ? contentGroupsResults.data.hasMore
              : null
          }
          types={contentGroupsQuery.state.mediaTypes}
          showAlert={showAlert}
          totalGroups={
            contentGroupsResults.isSuccess
              ? contentGroupsResults.data.count
              : "-"
          }
          contentGroups={contentGroups}
          handleContentGroupsPageChange={() =>
            contentGroupsQuery.handleUpdatePage(
              contentGroupsQuery.state.page + 1,
            )
          }
          searchBar={
            <RealTimeSearchBar
              searchBarEnd
              query={contentGroupsQuery.state.appliedQuery}
              onQueryChange={contentGroupsQuery.handleUpdateQuery}
            />
          }
          filterMenu={
            <Fragment>
              <OrganizeButton
                onClick={handleClick}
                variant="outlined"
                color="primary"
                endIcon={<FontAwesomeIcon icon={faAngleDown} />}
                classes={{ endIcon: classes.endIcon }}
              >
                Type
              </OrganizeButton>
              <Menu
                id="filter-groups-by-type-menu"
                anchorEl={anchorElType}
                keepMounted
                open={Boolean(anchorElType)}
                onClose={handleTypeClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
              >
                <MenuItem onClick={event => event.stopPropagation()}>
                  <FormControlLabel
                    label="Anthology"
                    control={
                      <Checkbox
                        name={"anthology"}
                        checked={includes(
                          "anthology",
                          contentGroupsQuery.state.mediaTypes,
                        )}
                        onChange={onTypeClick("anthology")}
                        color="primary"
                      />
                    }
                  />
                </MenuItem>

                <MenuItem onClick={event => event.stopPropagation()}>
                  <FormControlLabel
                    label="Collection"
                    control={
                      <Checkbox
                        name={"collection"}
                        checked={includes(
                          "collection",
                          contentGroupsQuery.state.mediaTypes,
                        )}
                        onChange={onTypeClick("collection")}
                        color="primary"
                      />
                    }
                  />
                </MenuItem>
              </Menu>
            </Fragment>
          }
        />
      }
      filesPanel={
        <ContentGroupFilesBrowser
          title="Unorganized Files"
          searchState={contentSearchState}
          sourceFilter={searchQuery.state.source}
          searchBar={
            <RealTimeSearchBar
              searchBarEnd
              query={searchQuery.state.appliedQuery}
              onQueryChange={query => searchQuery.handleUpdateQuery(query)}
            />
          }
          filterMenu={
            <ContentSourcesAutocomplete
              setSource={searchQuery.handleUpdateSource}
              source={searchQuery.state.source}
            />
          }
          loading={contentResults.isLoading}
          page={searchQuery.state.page}
          content={contentResults.isSuccess ? contentResults.data.contents : []}
          total={contentResults.isSuccess ? contentResults.data.count : "-"}
          handlePageChange={(event, newPage) =>
            searchQuery.handleUpdatePage(newPage)
          }
          handleSortChange={searchQuery.handleUpdateSort}
          onRowsPerPageChange={searchQuery.handleUpdateLimit}
          rowsPerPage={searchQuery.state.limit}
          order={searchQuery.state.orderDirection}
          orderBy={searchQuery.state.orderBy}
          selectedContent={selectedContent}
          SnackAlertHandler={SnackAlertHandler}
        />
      }
    />
  );
};

export default UngroupedItemsBrowser;
